// Compromisos MOCK del cobrador `C-01`, portados del `<script>` del mockup
// (`surekeep/mockups/_shared/cobranza-mock-events.js`: `compromisos`, `creditosExtra`, `historial`).
// No hay endpoint que los devuelva todavía: toda la pantalla va bajo `<MockBoundary>`.
//
// El mock congela su hoy en `SK.HOY = '2026-07-06'`; aquí los días se arman relativos al día que
// se le pase, para que las pastillas Hoy y Mañana sigan diciendo algo cuando se abra la pantalla.
import { shiftDay } from './logic';
import type { PaymentPromiseRow, PromiseCaseContact } from './types';

/** El cobrador del mockup: Roberto Sánchez, Zona 1. La pantalla solo muestra SUS compromisos. */
export const FIXTURE_COLLECTOR_ID = 'C-01';

/** Contexto del crédito: mora y nombre del acreditado, de `creditosExtra` y `clientesExtra`. */
interface CreditContext {
    customerId: string;
    customerName: string;
    arrearsDays: number;
    arrearsAmountCents: number;
}

const CREDIT_CONTEXT: Record<string, CreditContext> = {
    'CR-3001': {
        customerId: 'CL-3001',
        customerName: 'Guadalupe Martínez',
        arrearsDays: 42,
        arrearsAmountCents: 89_000,
    },
    'CR-3002': {
        customerId: 'CL-3002',
        customerName: 'Ricardo Pérez López',
        arrearsDays: 15,
        arrearsAmountCents: 46_000,
    },
    'CR-3003': {
        customerId: 'CL-3003',
        customerName: 'Ana María Ruiz',
        arrearsDays: 5,
        arrearsAmountCents: 32_000,
    },
    'CR-3004': {
        customerId: 'CL-3004',
        customerName: 'Fernando Ortega',
        arrearsDays: 62,
        arrearsAmountCents: 124_000,
    },
    'CR-3005': {
        customerId: 'CL-3005',
        customerName: 'Laura Villareal',
        arrearsDays: 3,
        arrearsAmountCents: 26_000,
    },
    'CR-3006': {
        customerId: 'CL-3006',
        customerName: 'Miguel Ángel Reyes',
        arrearsDays: 22,
        arrearsAmountCents: 52_000,
    },
    'CR-3007': {
        customerId: 'CL-3007',
        customerName: 'Patricia Domínguez',
        arrearsDays: 1,
        arrearsAmountCents: 22_000,
    },
    'CR-3009': {
        customerId: 'CL-3009',
        customerName: 'Beatriz Salazar',
        arrearsDays: 8,
        arrearsAmountCents: 38_000,
    },
    'CR-3010': {
        customerId: 'CL-3010',
        customerName: 'Héctor Núñez',
        arrearsDays: 91,
        arrearsAmountCents: 182_000,
    },
    'CR-3012': {
        customerId: 'CL-3012',
        customerName: 'Carlos Alberto Vargas',
        arrearsDays: 12,
        arrearsAmountCents: 48_000,
    },
};

/** Un contacto del histórico, con el día expresado como corrimiento contra hoy. */
interface ContactSeed {
    dayOffset: number;
    channel: string;
    outcome: string;
    note: string;
}

/**
 * El histórico de contactos por crédito, YA del más reciente al más viejo — el mismo orden en que
 * el mock lo pinta. El resultado va en español corriente («habló», no el `hablo` crudo del mock).
 */
const CASE_HISTORY: Record<string, readonly ContactSeed[]> = {
    'CR-3001': [
        { dayOffset: 0, channel: 'llamada', outcome: 'no contesta', note: '' },
        { dayOffset: 0, channel: 'llamada', outcome: 'no contesta', note: '' },
        { dayOffset: -5, channel: 'llamada', outcome: 'no contesta', note: '' },
        {
            dayOffset: -8,
            channel: 'sms',
            outcome: 'enviado',
            note: 'Blaster día 30 tras bloqueo',
        },
    ],
    'CR-3002': [
        {
            dayOffset: 0,
            channel: 'llamada',
            outcome: 'promesa',
            note: 'Pagará $460 en OXXO hoy mismo',
        },
        {
            dayOffset: -2,
            channel: 'llamada',
            outcome: 'habló',
            note: 'Cliente indica quincena el 8',
        },
    ],
    'CR-3003': [{ dayOffset: -1, channel: 'chat', outcome: 'habló', note: 'Confirmó pago OXXO' }],
    'CR-3004': [
        {
            dayOffset: 0,
            channel: 'llamada',
            outcome: 'promesa',
            note: '$620 hoy vía OXXO. Segundo compromiso.',
        },
        {
            dayOffset: -3,
            channel: 'llamada',
            outcome: 'habló',
            note: 'Solicita reestructura por baja de ingresos',
        },
        { dayOffset: -6, channel: 'llamada', outcome: 'no contesta', note: '' },
    ],
    'CR-3005': [{ dayOffset: -6, channel: 'sms', outcome: 'enviado', note: 'Aviso automático' }],
    'CR-3006': [
        {
            dayOffset: 0,
            channel: 'chat',
            outcome: 'habló',
            note: 'Cliente reporta pago hecho en tienda',
        },
    ],
    'CR-3007': [
        { dayOffset: 0, channel: 'llamada', outcome: 'promesa', note: 'Pagará hoy en tienda' },
    ],
    'CR-3009': [
        { dayOffset: -1, channel: 'llamada', outcome: 'promesa', note: 'App mañana temprano' },
    ],
    'CR-3010': [
        { dayOffset: 0, channel: 'whatsapp', outcome: 'enviado', note: 'Sin respuesta' },
        { dayOffset: 0, channel: 'llamada', outcome: 'no contesta', note: '' },
        { dayOffset: 0, channel: 'llamada', outcome: 'no contesta', note: '' },
        { dayOffset: -2, channel: 'llamada', outcome: 'promesa', note: 'Prometió $720 el 5' },
        { dayOffset: -4, channel: 'whatsapp', outcome: 'habló', note: 'Cliente pide plazo' },
    ],
    'CR-3012': [{ dayOffset: -2, channel: 'llamada', outcome: 'promesa', note: 'OXXO el martes' }],
};

/** Un compromiso, con sus dos días expresados como corrimiento contra hoy. */
interface PromiseSeed {
    promiseId: string;
    creditId: string;
    dueOffset: number;
    amountCents: number;
    channel: PaymentPromiseRow['channel'];
    status: PaymentPromiseRow['status'];
    registeredOffset: number;
}

/** Los 14 compromisos de `C-01`: 3 venciendo hoy, 3 mañana, 2 de ayer y 6 de histórico cerrado. */
const PROMISE_SEEDS: readonly PromiseSeed[] = [
    {
        promiseId: 'PR-2001',
        creditId: 'CR-3004',
        dueOffset: 0,
        amountCents: 62_000,
        channel: 'oxxo',
        status: 'active',
        registeredOffset: -2,
    },
    {
        promiseId: 'PR-2002',
        creditId: 'CR-3006',
        dueOffset: 0,
        amountCents: 26_000,
        channel: 'store',
        status: 'kept',
        registeredOffset: -3,
    },
    {
        promiseId: 'PR-2003',
        creditId: 'CR-3002',
        dueOffset: 0,
        amountCents: 46_000,
        channel: 'oxxo',
        status: 'active',
        registeredOffset: -1,
    },
    {
        promiseId: 'PR-2004',
        creditId: 'CR-3009',
        dueOffset: 1,
        amountCents: 38_000,
        channel: 'app',
        status: 'active',
        registeredOffset: 0,
    },
    {
        promiseId: 'PR-2005',
        creditId: 'CR-3012',
        dueOffset: 1,
        amountCents: 48_000,
        channel: 'oxxo',
        status: 'active',
        registeredOffset: 0,
    },
    {
        promiseId: 'PR-2006',
        creditId: 'CR-3001',
        dueOffset: 1,
        amountCents: 50_000,
        channel: 'store',
        status: 'active',
        registeredOffset: -1,
    },
    {
        promiseId: 'PR-2007',
        creditId: 'CR-3010',
        dueOffset: -1,
        amountCents: 72_000,
        channel: 'oxxo',
        status: 'broken',
        registeredOffset: -4,
    },
    {
        promiseId: 'PR-2008',
        creditId: 'CR-3007',
        dueOffset: -1,
        amountCents: 22_000,
        channel: 'store',
        status: 'kept',
        registeredOffset: -3,
    },
    {
        promiseId: 'PR-2009',
        creditId: 'CR-3003',
        dueOffset: -5,
        amountCents: 32_000,
        channel: 'store',
        status: 'kept',
        registeredOffset: -8,
    },
    {
        promiseId: 'PR-2010',
        creditId: 'CR-3005',
        dueOffset: -8,
        amountCents: 26_000,
        channel: 'oxxo',
        status: 'kept',
        registeredOffset: -12,
    },
    {
        promiseId: 'PR-2011',
        creditId: 'CR-3006',
        dueOffset: -14,
        amountCents: 26_000,
        channel: 'store',
        status: 'broken',
        registeredOffset: -17,
    },
    {
        promiseId: 'PR-2012',
        creditId: 'CR-3002',
        dueOffset: -20,
        amountCents: 46_000,
        channel: 'app',
        status: 'kept',
        registeredOffset: -23,
    },
    {
        promiseId: 'PR-2013',
        creditId: 'CR-3001',
        dueOffset: -12,
        amountCents: 50_000,
        channel: 'oxxo',
        status: 'broken',
        registeredOffset: -16,
    },
    {
        promiseId: 'PR-2014',
        creditId: 'CR-3004',
        dueOffset: -25,
        amountCents: 62_000,
        channel: 'store',
        status: 'kept',
        registeredOffset: -28,
    },
];

function buildCaseHistory(creditId: string, today: string): PromiseCaseContact[] {
    return (CASE_HISTORY[creditId] ?? []).map((contact) => ({
        day: shiftDay(today, contact.dayOffset),
        channel: contact.channel,
        outcome: contact.outcome,
        note: contact.note,
    }));
}

/** Los compromisos de `C-01` anclados al día que se le pase, en `YYYY-MM-DD`. */
export function buildPromisesFixture(today: string): PaymentPromiseRow[] {
    return PROMISE_SEEDS.map((seed) => {
        const context = CREDIT_CONTEXT[seed.creditId];
        return {
            promiseId: seed.promiseId,
            creditId: seed.creditId,
            customerId: context.customerId,
            customerName: context.customerName,
            promisedDay: shiftDay(today, seed.dueOffset),
            promisedAmountCents: seed.amountCents,
            channel: seed.channel,
            status: seed.status,
            registeredDay: shiftDay(today, seed.registeredOffset),
            arrearsDays: context.arrearsDays,
            arrearsAmountCents: context.arrearsAmountCents,
            caseHistory: buildCaseHistory(seed.creditId, today),
        };
    });
}
